import * as React from "react";
import {useState} from "react";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {AdminAnalyticsApi, IDailyLoginRow, ILoginTabResponse} from "../../../../api/AdminAnalyticsApi";
import {LoadingSpinner} from "../../../icons/LoadingSpinner.component";
import {formatDateLabel, formatMonthLabel} from "../shared/analytics.format";
import {
    axisTickStyle,
    chartColors,
    gridProps,
    gridStroke,
    legendStyle,
    tooltipStyle,
} from "../shared/analytics.constants";

interface ILoginByMonthChartProps {
    monthlyLogins: ILoginTabResponse["monthlyLogins"];
    country: string;
}

export const LoginByMonthChart: React.FC<ILoginByMonthChartProps> = (props) => {
    const [drilledMonth, setDrilledMonth] = useState<string | null>(null);
    const [dailyLogins, setDailyLogins] = useState<IDailyLoginRow[]>([]);
    const [dailyLoading, setDailyLoading] = useState<boolean>(false);
    const [dailyError, setDailyError] = useState<string | null>(null);

    const handleMonthBarClick = (month: string) => {
        const [year, monthNum] = month.split("-").map(Number);
        const startDate = `${year}-${String(monthNum).padStart(2, "0")}-01`;
        const lastDay = new Date(year, monthNum, 0).getDate();
        const endDate = `${year}-${String(monthNum).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        setDrilledMonth(month);
        setDailyLoading(true);
        setDailyError(null);
        AdminAnalyticsApi.getDailyLogins(startDate, endDate, props.country || undefined)
            .then((result) => {
                const raw = Array.isArray(result.data) ? result.data : [];
                const rows = raw.map((r) => ({
                    ...r,
                    external: r.external ?? (r.client ?? 0) + (r.thirdParty ?? 0),
                    internal: r.internal ?? 0,
                }));
                setDailyLogins(rows);
                setDailyLoading(false);
            })
            .catch(() => {
                setDailyError("Could not load daily logins. Please try again.");
                setDailyLogins([]);
                setDailyLoading(false);
            });
    };

    const handleBackToMonthly = () => {
        setDrilledMonth(null);
        setDailyLogins([]);
        setDailyError(null);
    };

    return (
        <div>
            <h3 className="admin-analytics__subsection-title">
                {drilledMonth ? `Daily Logins for ${formatMonthLabel(drilledMonth)}` : "Logins by Month"}
            </h3>
            {!drilledMonth &&
                <p className="admin-analytics__chart-hint">Click a month bar to drill down to daily
                    logins.</p>}
            {drilledMonth &&
                <button className="admin-analytics__date-btn" onClick={handleBackToMonthly}>Back to Monthly
                    View</button>}
            <div className="admin-analytics__chart">
                {dailyLoading ? (
                    <div className="admin-analytics__loading"><LoadingSpinner/></div>
                ) : dailyError ? (
                    <div className="admin-analytics__chart-error">{dailyError}</div>
                ) : drilledMonth && dailyLogins.length === 0 ? (
                    <div className="admin-analytics__chart-empty">No logins recorded for this month.</div>
                ) : (
                    <ResponsiveContainer width="100%" height={280}>
                        {!drilledMonth ? (
                            <BarChart data={props.monthlyLogins}
                                      onClick={(state: any) => state?.activeLabel
                                          && handleMonthBarClick(String(state.activeLabel))}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={axisTickStyle}
                                       axisLine={{stroke: gridStroke}} tickLine={false}/>
                                <YAxis tick={axisTickStyle} axisLine={false} tickLine={false}/>
                                <Tooltip
                                    labelFormatter={(label) => formatMonthLabel(label as string)}
                                    {...tooltipStyle} />
                                <Legend {...legendStyle} />
                                <Bar dataKey="internal" stackId="a" fill={chartColors.navy} name="Internal"
                                     cursor="pointer"/>
                                <Bar dataKey="external" stackId="a" fill={chartColors.tealLight}
                                     name="External" cursor="pointer"/>
                            </BarChart>
                        ) : (
                            <BarChart data={dailyLogins}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={axisTickStyle}
                                       axisLine={{stroke: gridStroke}} tickLine={false}/>
                                <YAxis tick={axisTickStyle} axisLine={false} tickLine={false}/>
                                <Tooltip
                                    labelFormatter={(label) => formatDateLabel(label as string)}
                                    {...tooltipStyle} />
                                <Legend {...legendStyle} />
                                <Bar dataKey="internal" stackId="a" fill={chartColors.navy}
                                     name="Internal"/>
                                <Bar dataKey="external" stackId="a" fill={chartColors.tealLight}
                                     name="External"/>
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};
